import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCard, Key, Shield, AlertTriangle, CheckCircle,
  Loader2, Eye, EyeOff, ExternalLink, Settings, Save, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Accept either the tenant token or the super-admin token, depending on which
// portal the user is navigating from. The same JWT is valid on both paths.
const getAuthToken = () =>
  localStorage.getItem('super_admin_token') || localStorage.getItem('token');

export default function StripeSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  // Form state
  const [credentials, setCredentials] = useState({
    secret_key: '',
    publishable_key: '',
    webhook_secret: ''
  });
  const [priceId, setPriceId] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/billing/stripe/config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch Stripe config:', err);
      toast.error('Failed to load Stripe configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!credentials.secret_key || !credentials.publishable_key) {
      toast.error('Secret Key and Publishable Key are required');
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API_BASE_URL}/api/billing/stripe/config/credentials`,
        credentials,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stripe credentials saved successfully');
      setShowCredentialsDialog(false);
      setCredentials({ secret_key: '', publishable_key: '', webhook_secret: '' });
      fetchConfig();
    } catch (err) {
      console.error('Failed to save credentials:', err);
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm('Are you sure you want to remove Stripe credentials? This will disable Stripe billing.')) {
      return;
    }

    try {
      const token = getAuthToken();
      await axios.delete(
        `${API_BASE_URL}/api/billing/stripe/config/credentials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stripe credentials removed');
      fetchConfig();
    } catch (err) {
      toast.error('Failed to remove credentials');
    }
  };

  const handleSavePriceId = async () => {
    if (!priceId || !selectedPlan) {
      toast.error('Price ID is required');
      return;
    }

    setSaving(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API_BASE_URL}/api/billing/stripe/config/plan-price`,
        {
          plan_id: selectedPlan.id,
          stripe_price_id: priceId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Price ID saved for ${selectedPlan.name} plan`);
      setShowPriceDialog(false);
      setPriceId('');
      setSelectedPlan(null);
      fetchConfig();
    } catch (err) {
      console.error('Failed to save price ID:', err);
      toast.error(err.response?.data?.detail || 'Failed to save price ID');
    } finally {
      setSaving(false);
    }
  };

  const openPriceDialog = (plan) => {
    setSelectedPlan(plan);
    setPriceId(plan.stripe_price_id || '');
    setShowPriceDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-wt-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg p-6" data-testid="stripe-settings-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-wt-primary" />
            Stripe Billing Settings
          </h1>
          <p className="text-gray-400">
            Configure Stripe for direct subscription billing
          </p>
        </div>

        {/* Status Card */}
        <Card className="bg-wt-surface border-white/10 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-wt-primary" />
                  API Credentials
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Stripe API keys for processing payments
                </CardDescription>
              </div>
              {config?.is_configured ? (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Configured
                </Badge>
              ) : (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Not Configured
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {config?.is_configured ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-wt-bg p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Secret Key</p>
                    <p className="text-white font-mono">{config.secret_key_masked}</p>
                  </div>
                  <div className="bg-wt-bg p-4 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Mode</p>
                    <Badge className={config.is_test_mode 
                      ? "bg-yellow-500/20 text-yellow-400" 
                      : "bg-green-500/20 text-green-400"
                    }>
                      {config.is_test_mode ? 'Test Mode' : 'Live Mode'}
                    </Badge>
                  </div>
                </div>
                
                <div className="bg-wt-bg p-4 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Publishable Key</p>
                  <p className="text-white font-mono text-sm break-all">
                    {config.publishable_key || 'Not set'}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowCredentialsDialog(true)}
                    className="bg-wt-primary"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Update Credentials
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteCredentials}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Alert className="bg-yellow-500/10 border-yellow-500/30">
                  <AlertTriangle className="h-4 w-4 text-yellow-400" />
                  <AlertDescription className="text-yellow-300">
                    Stripe is not configured. Add your API credentials to enable billing.
                  </AlertDescription>
                </Alert>
                
                <Button
                  onClick={() => setShowCredentialsDialog(true)}
                  className="bg-wt-primary"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Add Stripe Credentials
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plans Configuration */}
        <Card className="bg-wt-surface border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Plan Configuration</CardTitle>
            <CardDescription className="text-gray-400">
              Map your Stripe Price IDs to subscription plans. 
              <a 
                href="https://dashboard.stripe.com/products" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-wt-primary hover:underline ml-1 inline-flex items-center gap-1"
              >
                Create products in Stripe
                <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {config?.available_plans?.map((plan) => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between p-4 bg-wt-bg rounded-lg border border-white/5"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-medium">{plan.name}</h3>
                      <Badge className="bg-wt-primary/20 text-wt-primary">
                        ${plan.price}/month
                      </Badge>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">
                      {plan.features.slice(0, 2).join(' • ')}
                    </p>
                    {plan.stripe_price_id && (
                      <p className="text-green-400 text-xs mt-2 font-mono">
                        {plan.stripe_price_id}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {plan.stripe_price_id ? (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Configured
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-500/20 text-gray-400">
                        Not Set
                      </Badge>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPriceDialog(plan)}
                      className="border-white/10"
                      disabled={!config?.is_configured}
                    >
                      {plan.stripe_price_id ? 'Edit' : 'Set'} Price ID
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {!config?.is_configured && (
              <p className="text-gray-500 text-sm mt-4 text-center">
                Configure Stripe credentials first to set up plan prices.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="bg-wt-surface border-white/10 mt-6">
          <CardHeader>
            <CardTitle className="text-white text-lg">Setup Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
              <li>
                Go to{' '}
                <a 
                  href="https://dashboard.stripe.com/apikeys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-wt-primary hover:underline"
                >
                  Stripe API Keys
                </a>
                {' '}and copy your Secret Key and Publishable Key
              </li>
              <li>Add the credentials above using the "Add Stripe Credentials" button</li>
              <li>
                Create Products and Prices in{' '}
                <a 
                  href="https://dashboard.stripe.com/products" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-wt-primary hover:underline"
                >
                  Stripe Products
                </a>
                {' '}for each plan (Standard $29/mo, Pro $99/mo)
              </li>
              <li>Copy the Price ID (starts with <code className="bg-wt-bg px-1 rounded">price_</code>) for each product</li>
              <li>Configure the Price IDs for each plan above</li>
              <li>Companies can now subscribe via the Billing page</li>
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Credentials Dialog */}
      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="bg-wt-surface border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-wt-primary" />
              Stripe API Credentials
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter your Stripe API keys. These will be encrypted and stored securely.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Secret Key *</Label>
              <div className="relative">
                <Input
                  type={showSecretKey ? "text" : "password"}
                  value={credentials.secret_key}
                  onChange={(e) => setCredentials(prev => ({ ...prev, secret_key: e.target.value }))}
                  placeholder="sk_live_... or sk_test_..."
                  className="bg-wt-bg border-white/10 pr-10 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Publishable Key *</Label>
              <Input
                type="text"
                value={credentials.publishable_key}
                onChange={(e) => setCredentials(prev => ({ ...prev, publishable_key: e.target.value }))}
                placeholder="pk_live_... or pk_test_..."
                className="bg-wt-bg border-white/10 font-mono text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Webhook Secret (optional)</Label>
              <Input
                type="text"
                value={credentials.webhook_secret}
                onChange={(e) => setCredentials(prev => ({ ...prev, webhook_secret: e.target.value }))}
                placeholder="whsec_..."
                className="bg-wt-bg border-white/10 font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                Required for automatic subscription updates via webhooks
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCredentialsDialog(false)}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveCredentials}
              disabled={saving}
              className="bg-wt-primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Credentials
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Price ID Dialog */}
      <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
        <DialogContent className="bg-wt-surface border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Configure {selectedPlan?.name} Plan
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter the Stripe Price ID for this plan
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-wt-bg p-3 rounded-lg">
              <p className="text-white font-medium">{selectedPlan?.name}</p>
              <p className="text-wt-primary font-bold">${selectedPlan?.price}/month</p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Stripe Price ID</Label>
              <Input
                type="text"
                value={priceId}
                onChange={(e) => setPriceId(e.target.value)}
                placeholder="price_..."
                className="bg-wt-bg border-white/10 font-mono"
              />
              <p className="text-xs text-gray-500">
                Find this in{' '}
                <a 
                  href="https://dashboard.stripe.com/products" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-wt-primary hover:underline"
                >
                  Stripe Dashboard → Products
                </a>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPriceDialog(false)}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePriceId}
              disabled={saving || !priceId}
              className="bg-wt-primary"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
